import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NoTokensBanner from './NoTokensBanner';
import { TarotCard } from '../data/tarotCards';
import { SpreadType } from '../data/spreadTypes';
import { useTheme } from '../contexts/ThemeContext';

interface CardSelectionGridProps {
  spreadType: SpreadType;
  onCardSelect: (card: TarotCard, position: number) => void;
  selectedCards: { card: TarotCard; position: number; isReversed: boolean }[];
  isSelectionComplete: boolean;
  availableCards: TarotCard[];
  onStartReading: () => void;
  isGenerating?: boolean;
  error?: string | null;
}

// Cada phantom tem timing próprio para parecer orgânico
interface PhantomCard {
  x: number;
  y: number;
  rotate: number;
  spreadDuration: number;
  spreadDelay: number;
  gatherDelay: number;
}

const PHANTOM_COUNT = 12;

type ShufflePhase = 'idle' | 'spread' | 'gather';

/**
 * Dimensões do "tabuleiro" onde as cartas ocupam a posição exata da tiragem
 * (mesmo layout dos diagramas oficiais — Cruz Celta, Mandala etc.) e do
 * baralho abaixo dele. Calculadas a partir do viewport pra que tabuleiro +
 * baralho SEMPRE caibam na tela sem precisar rolar — em vez de manter um
 * tamanho fixo de carta e deixar a página crescer a cada carta comprada
 * (era o que empurrava o baralho pra fora da tela no celular).
 */
const getLayoutDims = (spreadType: SpreadType) => {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 375;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
  const isMobile = vw < 640;

  // Baralho: tamanho da carta do topo + a linha "N no baralho · Embaralhar" logo abaixo.
  // deckContainerH precisa caber a carta INTEIRA (deckCardH) — antes era um valor fixo
  // menor que deckCardH (bug real em produção: a carta, position:absolute e centralizada,
  // transbordava do container e sobrepunha visualmente a linha do baralho/botão abaixo;
  // pior no desktop, ~95px de transbordo).
  const deckCardW = isMobile ? 84 : 150;
  const deckCardH = deckCardW * 1.5;
  const deckRowH = isMobile ? 34 : 42; // altura da linha "N no baralho · Embaralhar"
  const deckGap = isMobile ? 8 : 12; // gap-2/gap-3 do flex-col entre a carta e essa linha
  const deckContainerH = deckCardH;
  const deckAreaH = deckContainerH + deckGap + deckRowH;

  // Espaço reservado fora do tabuleiro: header da página + título/contador
  // deste componente + área do baralho (ou botão final) + barra de progresso.
  const headerReserve = isMobile ? 84 : 130;
  const footerReserve = isMobile ? 34 : 50;
  const reservedH = headerReserve + deckAreaH + footerReserve;

  const maxBoardH = Math.max(160, vh - reservedH);
  // A página (ConsultationPage) tem padding horizontal de 16px de cada lado
  // (px-4) — subtrai uma margem um pouco maior pra garantir zero overflow.
  const maxBoardW = Math.min(vw - (isMobile ? 40 : 64), isMobile ? 420 : 560);

  let boardW = maxBoardW;
  let boardH = boardW / spreadType.boardAspect;
  if (boardH > maxBoardH) {
    boardH = maxBoardH;
    boardW = boardH * spreadType.boardAspect;
  }

  const cardW = Math.max(26, boardW * (spreadType.cardScale / 100));
  const cardH = cardW * 1.5;

  const deckContainerW = Math.min(vw - 32, isMobile ? 260 : 460);
  const deckSpreadX = deckContainerW * 0.36;
  const deckSpreadY = deckContainerH * 0.32;

  return { boardW, boardH, cardW, cardH, deckAreaH, deckCardW, deckCardH, deckContainerW, deckContainerH, deckSpreadX, deckSpreadY };
};

const CardSelectionGrid: React.FC<CardSelectionGridProps> = ({
  spreadType,
  onCardSelect,
  selectedCards,
  isSelectionComplete,
  availableCards,
  onStartReading,
  isGenerating = false,
  error = null,
}) => {
  const [deck, setDeck] = useState<TarotCard[]>([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shufflePhase, setShufflePhase] = useState<ShufflePhase>('idle');
  const [isDrawing, setIsDrawing] = useState(false);
  const [phantoms, setPhantoms] = useState<PhantomCard[]>([]);
  const [dims, setDims] = useState(() => getLayoutDims(spreadType));

  useEffect(() => {
    const onResize = () => setDims(getLayoutDims(spreadType));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [spreadType]);

  const { theme } = useTheme();
  const rawCardBack = theme.images.cardBackImageUrl ?? '';
  const cardBackUrl = (rawCardBack.startsWith('http://') || rawCardBack.startsWith('https://'))
    ? rawCardBack
    : undefined;

  useEffect(() => {
    const shuffled = [...availableCards].sort(() => Math.random() - 0.5);
    setDeck(shuffled);
  }, []);

  const buildShuffledDeck = () =>
    [...availableCards]
      .filter(c => !selectedCards.some(s => s.card.id === c.id))
      .sort(() => Math.random() - 0.5);

  const handleShuffle = () => {
    if (isShuffling || isDrawing || isSelectionComplete) return;

    const generated: PhantomCard[] = Array.from({ length: PHANTOM_COUNT }, (_, i) => ({
      x: (Math.random() - 0.5) * dims.deckSpreadX * 2,
      y: (Math.random() - 0.5) * dims.deckSpreadY * 2,
      rotate: (Math.random() - 0.5) * 80,
      spreadDuration: 0.38 + Math.random() * 0.38,
      spreadDelay: Math.random() * 0.14,
      gatherDelay: (PHANTOM_COUNT - i) * 0.018,
    }));
    setPhantoms(generated);
    setIsShuffling(true);
    setShufflePhase('spread');

    setTimeout(() => {
      setShufflePhase('gather');
      setTimeout(() => {
        setDeck(buildShuffledDeck());
        setShufflePhase('idle');
        setIsShuffling(false);
      }, 600);
    }, 850);
  };

  const handleDraw = () => {
    if (isShuffling || isDrawing || isSelectionComplete || deck.length === 0) return;
    const nextPosition = selectedCards.length;
    if (nextPosition >= spreadType.positions.length) return;

    setIsDrawing(true);
    const card = deck[0];
    setTimeout(() => {
      onCardSelect(card, nextPosition);
      setDeck(prev => prev.slice(1));
      setIsDrawing(false);
    }, 380);
  };

  // Verso da carta — absolute inset-0 para preencher qualquer container relativo
  const CardBack = ({ rounded = 'rounded-md' }: { rounded?: string }) =>
    cardBackUrl ? (
      <img
        src={cardBackUrl}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover ${rounded}`}
        style={{ transform: 'scale(1.12)', transformOrigin: 'center' }}
      />
    ) : (
      <div className={`absolute inset-0 bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center ${rounded}`}>
        <span className="text-gold-300" style={{ fontSize: Math.min(28, dims.deckCardW * 0.3) }}>🔮</span>
      </div>
    );

  const remainingNeeded = spreadType.positions.length - selectedCards.length;
  const numberFontSize = Math.max(9, Math.min(15, dims.cardW * 0.24));

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col items-center gap-2 sm:gap-4 py-1 sm:py-2">

      {/* Cabeçalho compacto */}
      <div className="text-center px-2">
        <h3 className="text-base sm:text-2xl font-mystical font-bold text-white leading-tight">
          Escolha suas cartas
        </h3>
        <p className="text-gray-300 text-[11px] sm:text-sm leading-snug">
          {isSelectionComplete
            ? 'Seleção completa!'
            : selectedCards.length === 0
            ? 'Embaralhe e clique no baralho para comprar sua carta'
            : `Compre mais ${remainingNeeded} carta${remainingNeeded > 1 ? 's' : ''}`}
        </p>
        <div className="text-gold-300 text-[11px] sm:text-sm">
          {selectedCards.length} / {spreadType.positions.length} cartas
        </div>
      </div>

      {/* Tabuleiro — cada carta ocupa a posição exata da tiragem (mesmo
          layout dos diagramas oficiais), do tamanho que cabe na tela. */}
      <div
        className="relative shrink-0"
        style={{ width: dims.boardW, height: dims.boardH }}
      >
        {spreadType.positions.map((pos, i) => {
          const sel = selectedCards[i];
          const filled = !!sel;
          return (
            <div
              key={pos.id}
              className="absolute"
              style={{
                width: dims.cardW,
                height: dims.cardH,
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                marginLeft: -dims.cardW / 2,
                marginTop: -dims.cardH / 2,
                transform: pos.rotate ? `rotate(${pos.rotate}deg)` : undefined,
                zIndex: filled ? 10 : 1,
              }}
            >
              {filled ? (
                <motion.div
                  initial={{ opacity: 0, rotateY: 90, scale: 0.8 }}
                  animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 170, damping: 20 }}
                  className="relative w-full h-full rounded-md overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 shadow-md shadow-black/50 border border-gold-400/40"
                  style={{ perspective: 800 }}
                >
                  {sel.card.image ? (
                    <img
                      src={sel.card.image}
                      alt={sel.card.name}
                      className={`w-full h-full object-cover ${sel.isReversed ? 'rotate-180' : ''}`}
                    />
                  ) : (
                    <div className={`absolute inset-0 flex items-center justify-center p-1 ${sel.isReversed ? 'rotate-180' : ''}`}>
                      <span className="text-gold-300 font-bold text-center leading-tight" style={{ fontSize: numberFontSize }}>
                        {sel.card.name}
                      </span>
                    </div>
                  )}
                  <span
                    className="absolute -top-1.5 -left-1.5 rounded-full bg-gold-500 text-black font-bold flex items-center justify-center shadow"
                    style={{ width: numberFontSize * 1.7, height: numberFontSize * 1.7, fontSize: numberFontSize * 0.85 }}
                  >
                    {i + 1}
                  </span>
                  {sel.isReversed && (
                    <div className="absolute bottom-0 left-0 right-0 bg-red-600/80 py-0.5 text-center">
                      <span className="text-white font-bold tracking-wide" style={{ fontSize: numberFontSize * 0.6 }}>INVERTIDA</span>
                    </div>
                  )}
                </motion.div>
              ) : (
                <div className="w-full h-full rounded-md border-2 border-dashed border-white/20 bg-white/[0.03] flex items-center justify-center">
                  <span className="text-white/25 font-mystical font-bold" style={{ fontSize: numberFontSize * 1.3 }}>
                    {i + 1}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Baralho — some quando seleção completa */}
      {!isSelectionComplete && (
        <div className="flex flex-col items-center gap-2 sm:gap-3" style={{ minHeight: dims.deckAreaH }}>
          <div
            className="relative"
            style={{ width: dims.deckContainerW, height: dims.deckContainerH }}
          >
            {/* Phantoms para animação de cascata */}
            {isShuffling && phantoms.map((p, i) => (
              <motion.div
                key={`ph-${i}`}
                className="absolute rounded-md overflow-hidden"
                style={{
                  width: dims.deckCardW,
                  height: dims.deckCardH,
                  top: '50%',
                  left: '50%',
                  marginTop: -dims.deckCardH / 2,
                  marginLeft: -dims.deckCardW / 2,
                  zIndex: i + 1,
                }}
                initial={{ x: 0, y: 0, rotate: 0, opacity: 0 }}
                animate={
                  shufflePhase === 'spread'
                    ? { x: p.x, y: p.y, rotate: p.rotate, opacity: 0.82 }
                    : { x: 0, y: 0, rotate: 0, opacity: 0 }
                }
                transition={
                  shufflePhase === 'spread'
                    ? { duration: p.spreadDuration, delay: p.spreadDelay, ease: [0.22, 0.68, 0.35, 1.0] }
                    : { type: 'spring', stiffness: 90, damping: 16, delay: p.gatherDelay }
                }
              >
                <CardBack />
              </motion.div>
            ))}

            {/* Camadas de profundidade (efeito de pilha) — centralizadas (bug real:
                tinham stagger +i*2 sempre pra baixo-direita, deixando o monte torto;
                a ilusão de profundidade agora vem só de escala/opacidade). */}
            {[4, 3, 2, 1].map(i => (
              <div
                key={`d-${i}`}
                className="absolute rounded-md overflow-hidden"
                style={{
                  width: dims.deckCardW,
                  height: dims.deckCardH,
                  top: '50%',
                  left: '50%',
                  marginTop: -dims.deckCardH / 2,
                  marginLeft: -dims.deckCardW / 2,
                  transform: `scale(${1 - i * 0.02})`,
                  zIndex: i,
                  opacity: 0.45 + i * 0.11,
                }}
              >
                <CardBack />
              </div>
            ))}

            {/* Carta do topo — clicável */}
            <motion.div
              className={`absolute rounded-md overflow-hidden ${isShuffling || isDrawing ? 'pointer-events-none' : 'cursor-pointer'}`}
              style={{
                width: dims.deckCardW,
                height: dims.deckCardH,
                top: '50%',
                left: '50%',
                marginTop: -dims.deckCardH / 2,
                marginLeft: -dims.deckCardW / 2,
                zIndex: 20,
              }}
              whileHover={!isShuffling && !isDrawing ? { y: -10, scale: 1.05, transition: { type: 'spring', stiffness: 300, damping: 22 } } : {}}
              whileTap={!isShuffling && !isDrawing ? { scale: 0.97 } : {}}
              animate={
                isShuffling
                  ? { opacity: 0, scale: 0.95 }
                  : isDrawing
                  ? { y: -90, opacity: 0, scale: 0.85, rotate: -4 }
                  : { opacity: 1, scale: 1, y: 0, rotate: 0 }
              }
              transition={isDrawing ? { type: 'spring', stiffness: 170, damping: 18 } : { duration: 0.25 }}
              onClick={handleDraw}
            >
              <CardBack />
            </motion.div>
          </div>

          <div className="flex items-center gap-3">
            <p className="text-purple-400 text-[10px] sm:text-xs text-center">
              {deck.length} no baralho · toque pra comprar
            </p>
            <motion.button
              onClick={handleShuffle}
              disabled={isShuffling || isDrawing}
              whileHover={!isShuffling && !isDrawing ? { scale: 1.05 } : {}}
              whileTap={!isShuffling && !isDrawing ? { scale: 0.93 } : {}}
              className={`px-3 py-1 sm:px-5 sm:py-1.5 rounded-full border text-[11px] sm:text-sm font-mystical transition-colors duration-200
                ${isShuffling || isDrawing
                  ? 'border-purple-900 text-purple-800 cursor-not-allowed'
                  : 'border-purple-500 text-purple-200 hover:border-gold-400 hover:text-gold-300'
                }`}
            >
              🔀 {isShuffling ? 'Embaralhando...' : 'Embaralhar'}
            </motion.button>
          </div>
        </div>
      )}

      {/* Botão Fazer Leitura — aparece quando todas as cartas estão selecionadas */}
      <AnimatePresence>
        {isSelectionComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="flex flex-col items-center gap-2 sm:gap-3"
            style={{ minHeight: dims.deckAreaH }}
          >
            <motion.button
              onClick={onStartReading}
              disabled={isGenerating}
              whileHover={!isGenerating ? { scale: 1.05 } : {}}
              whileTap={!isGenerating ? { scale: 0.97 } : {}}
              className="px-8 py-2.5 sm:px-10 sm:py-3 bg-gradient-to-r from-gold-600 to-gold-700 text-white font-semibold rounded-full shadow-lg shadow-gold-900/40 hover:from-gold-700 hover:to-gold-800 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 text-sm sm:text-base"
            >
              {isGenerating ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Os astros estão falando...
                </>
              ) : (
                <>✨ Fazer Leitura</>
              )}
            </motion.button>
            {error && error.toLowerCase().includes('insuficientes') ? (
              <NoTokensBanner error={error} />
            ) : error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-300 text-sm text-center max-w-xs"
              >
                {error}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Barra de progresso */}
      <div className="w-full max-w-xs px-4">
        <div className="w-full bg-gray-700 rounded-full h-1.5">
          <motion.div
            className="bg-gradient-to-r from-gold-500 to-gold-600 h-1.5 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(selectedCards.length / spreadType.positions.length) * 100}%` }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          />
        </div>
      </div>

    </div>
  );
};

export default CardSelectionGrid;
